import type Highcharts from "highcharts"
import type { ReactNode } from "react"

import {
  Badge,
  Button,
  PermissionTooltip,
  RadioGroup,
  RadioGroupItem,
} from "~/components/design-system/components"
import {
  BREAKDOWN_VIEW_OPTIONS,
  type BreakdownViewMode,
  breakdownViewSchema,
  signedPct,
  signedUnits,
} from "~/components/demand-2/shared/demandBreakdownShared"
import { ValueControl } from "~/components/demand-2/shared/FramingAnswerPicker"
import {
  adaptationLabels,
  KNOWN_SHARE_OPTION,
} from "~/components/demand-2/shared/preorderDevelopment"
import { STRATEGY_REQUIRES } from "~/components/demand-2/shared/strategyPermission"
import { Label } from "~/components/reusable/Label"
import SuperChart, {
  type DateChartSeries,
  type StackedBarChartData,
} from "~/components/reusable/graphs/SuperChart"
import { ChartType, LegendAlignment } from "~/constants/graphs"
import { usePermission } from "~/hooks/usePermission"
import type { CollectionKind } from "~/utils/collectionKind"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import type { AdaptationMode } from "~/utils/demandPreorderDevelopment"
import { formatDecimalToPercent, formatNumber } from "~/utils/formatting"
import { roundNumber } from "~/utils/helpers"
import { formatName } from "~/utils/namingUtils"

/**
 * The layout both kinds' "Pre-order development" breakdowns share: the four-column chart with its
 * weekly alternative, then the adaptation question. Each kind supplies its own figures and copy.
 */

/** The bordered chart box with its Aggregated / Weekly toggle. */
export const PreorderChartPanel = ({
  persistKey,
  idPrefix,
  series,
  columns,
  yAxisMax,
  weekly,
  footer,
}: {
  persistKey: string
  idPrefix: string
  series: StackedBarChartData[]
  columns: string[]
  yAxisMax: number | undefined
  /** Null where there is no weekly reading; the aggregated chart shows instead. */
  weekly: ReactNode | null
  footer?: ReactNode
}) => {
  const [view, setView] = useSafeLocalStorage<BreakdownViewMode>(
    `${persistKey}.view`,
    "aggregated",
    breakdownViewSchema,
  )
  return (
    <div className="flex flex-col overflow-hidden rounded-md border border-base bg-base">
      <div className="flex justify-end border-b border-base px-3 py-2">
        <RadioGroup
          value={view}
          onValueChange={(value) => setView(value as BreakdownViewMode)}
          className="flex flex-row flex-wrap justify-end gap-x-4 gap-y-2"
        >
          {BREAKDOWN_VIEW_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center gap-2">
              <RadioGroupItem id={`${idPrefix}-view-${option.value}`} value={option.value} />
              <Label htmlFor={`${idPrefix}-view-${option.value}`}>{option.label}</Label>
            </div>
          ))}
        </RadioGroup>
      </div>
      <div className="flex flex-col gap-2 p-2">
        {/*
          Distinct keys are load-bearing: without them React only swaps props, Highcharts
          MERGES on update, and the datetime axis leaks into the category one.
        */}
        {view === "aggregated" || weekly == null ? (
          <SuperChart
            key="aggregated"
            type={ChartType.StackedColumn}
            data={series}
            xCategories={columns}
            height={340}
            legendAlignment={LegendAlignment.Bottom}
            yAxisMax={yAxisMax}
            // Only the last column is a composition; naming the series on the others would
            // call the baseline "confirmed".
            plainTooltipCategories={columns.slice(0, -1)}
          />
        ) : (
          weekly
        )}
        {footer}
      </div>
    </div>
  )
}

/** "What are your expectations…": the kind's answers, each with what it comes to on this scope. */
export const PreorderAdaptationQuestion = <M extends AdaptationMode>({
  kind,
  idPrefix,
  knownShare,
  modes,
  notes,
  baseline,
  projectedByMode,
  mode,
  modeAnswered,
  modeInherited,
  narrow,
  paceNote,
  setMode,
  clearMode,
}: {
  kind: CollectionKind
  idPrefix: string
  /** The share confirmed by now; the question asks about the rest. */
  knownShare: number
  /** The answers offered, in display order. */
  modes: readonly M[]
  notes: Record<M, string>
  /** The baseline's own pre-orders, what each answer is stated against, as the engine applies it. */
  baseline: number
  projectedByMode: Record<AdaptationMode, number>
  mode: AdaptationMode
  modeAnswered: boolean
  modeInherited: boolean
  /** A node, slice or variant, which follows the collection until it answers on its own. */
  narrow: boolean
  /** Shown when the book can't be read as a trend. */
  paceNote: string | null
  setMode: (mode: M) => void
  clearMode: () => void
}) => {
  const own = modeAnswered && !modeInherited
  const title = `What are your expectations of the ${formatDecimalToPercent(Math.max(1 - knownShare, 0), 1)} of ${formatName("preorder", true)} that are still to come?`
  // RadioGroup has no `requires`: PermissionTooltip over it plus its own `disabled`.
  const { allowed: canEdit } = usePermission(STRATEGY_REQUIRES)
  const labels = adaptationLabels(kind)
  return (
    <section className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="label-sm text-fg-base">{title}</span>
        {!modeAnswered && !narrow && (
          <Badge variant="gray" size="xs">
            Not answered
          </Badge>
        )}
        {narrow && !own && (
          <Badge variant="gray" size="xs">
            Follows overall
          </Badge>
        )}
        {narrow && own && (
          <Button variant="transparent" size="xs" requires={STRATEGY_REQUIRES} onClick={clearMode}>
            Follow overall
          </Button>
        )}
      </div>
      {/* No radio pre-selected while unanswered: Radix fires no change on clicking the selected
          value, so a planner agreeing with the fallback could never record it. */}
      {!modeAnswered && (
        <p className="max-w-[50ch] paragraph-sm text-fg-muted">
          {narrow
            ? `Follows the collection, which has not answered yet. The plan follows “${labels[mode]}” until someone does.`
            : `The plan follows “${labels[mode]}” until you answer.`}
        </p>
      )}
      {paceNote && <p className="paragraph-sm text-fg-subtle">{paceNote}</p>}
      <PermissionTooltip requires={STRATEGY_REQUIRES} wrapperClassName="w-full">
        <RadioGroup
          value={modeAnswered || narrow ? mode : ""}
          disabled={!canEdit}
          onValueChange={(value) => setMode(value as M)}
          className="divide-y divide-border-base"
        >
          {modes.map((m) => {
            const optionProjected = projectedByMode[m]
            const optionDiff = optionProjected - baseline
            const optionPct = baseline > 0 ? optionDiff / baseline : null
            return (
              <div key={m} className="flex items-start gap-3 py-3">
                <RadioGroupItem id={`${idPrefix}-adaptation-${m}`} value={m} />
                <div className="flex flex-1 flex-col gap-1">
                  <Label htmlFor={`${idPrefix}-adaptation-${m}`}>{labels[m]}</Label>
                  <p className="paragraph-sm text-fg-subtle">{notes[m]}</p>
                  <p className="paragraph-sm text-fg-subtle">
                    <span
                      className={optionDiff >= 0 ? "text-fg-success-icon" : "text-fg-error-icon"}
                    >
                      {signedUnits(optionDiff)} units
                      {optionPct == null ? "" : ` · ${signedPct(optionPct)}`}
                    </span>{" "}
                    vs the baseline{" · "}
                    <span className="text-fg-base">
                      {formatNumber(Math.round(optionProjected))}
                    </span>{" "}
                    projected
                  </p>
                </div>
              </div>
            )
          })}
        </RadioGroup>
      </PermissionTooltip>
    </section>
  )
}

type KnownShareState = "locked" | "overridden" | "estimated"

const knownShareStateOf = (locked: boolean, overrideShare: number | null): KnownShareState => {
  if (locked) return "locked"
  return overrideShare == null ? "estimated" : "overridden"
}

const KNOWN_SHARE_BADGES: Record<KnownShareState, { label: string; variant: "gray" | "violet" }> = {
  locked: { label: "Closed", variant: "gray" },
  overridden: { label: "Set by you", variant: "violet" },
  estimated: { label: "Estimated", variant: "gray" },
}

const KNOWN_SHARE_VALUE = KNOWN_SHARE_OPTION.value!

/** A kind's wording for `KnownShareBar`. */
export type KnownShareCopy = {
  label: string
  /** The reset's tooltip, given the estimated share as a percentage. */
  resetTitle: (estimated: string) => string
}

/**
 * The share of the pre-orders confirmed by now (`preorderKnownShare`), the pace denominator: fashion's
 * collection curve, NOOS's pre-order profile over the remaining weeks. Overriding moves the
 * expectation, never the pre-orders.
 */
export const KnownShareBar = ({
  knownShare,
  measuredKnownShare,
  overrideShare,
  onChange,
  copy,
  readOnly = false,
  lockedReason,
}: {
  knownShare: number
  measuredKnownShare: number
  overrideShare: number | null
  onChange: (share: number | null) => void
  copy: KnownShareCopy
  /** For a single variant: the share is set per collection or node, not per style. */
  readOnly?: boolean
  /** Pins the bar at 100% with this reason: an answer that already treats every pre-order as confirmed. */
  lockedReason?: string
}) => {
  const state = knownShareStateOf(lockedReason != null, overrideShare)
  const shown = state === "locked" ? 1 : knownShare
  const badge = KNOWN_SHARE_BADGES[state]
  return (
    <ValueControl
      key={state === "overridden" ? overrideShare! : state}
      value={{ ...KNOWN_SHARE_VALUE, label: copy.label }}
      pct={roundNumber(shown * 100, 1)}
      disabled={readOnly || state === "locked"}
      disabledTitle={
        lockedReason ??
        (readOnly
          ? `Set for the whole collection under ${formatName("preorder", false, true)} development in Demand configuration.`
          : undefined)
      }
      onCommit={(pct) => onChange(pct / 100)}
      badge={
        <Badge variant={badge.variant} size="xs">
          {badge.label}
        </Badge>
      }
      actions={
        !readOnly &&
        state === "overridden" && (
          <Button
            variant="transparent"
            size="xs"
            requires={STRATEGY_REQUIRES}
            allowedTitle={copy.resetTitle(formatDecimalToPercent(measuredKnownShare, 1))}
            onClick={() => onChange(null)}
          >
            Reset
          </Button>
        )
      }
      sliderStep={0.5}
      sliderMarker={
        // The estimated share. Radix insets thumb travel by half a thumb each end, so a flat
        // `left: p%` would drift from the thumb. No pointer events, or it blocks dragging.
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 h-3 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-fg-base"
          style={{
            left: `calc(8px + ${Math.min(Math.max(measuredKnownShare, 0), 1)} * (100% - 16px))`,
          }}
        />
      }
      footer={
        <div className="flex items-baseline justify-between gap-3">
          <span className="paragraph-sm text-fg-subtle">
            {formatDecimalToPercent(shown, 1)} confirmed
          </span>
          <span className="paragraph-sm text-fg-subtle">
            {formatDecimalToPercent(Math.max(1 - shown, 0), 1)} still to come
          </span>
        </div>
      }
    />
  )
}

/** The weekly chart's axes: units on the left, the confirmed share on the right. */
export const PREORDER_WEEKLY_Y_AXES: Highcharts.YAxisOptions[] = [
  { title: { text: undefined }, allowDecimals: false, min: 0 },
  {
    title: { text: undefined },
    labels: { format: "{value} %", style: { fontSize: "12px" } },
    opposite: true,
    min: 0,
    max: 100,
  },
]

/** The weekly confirmed-share line on the right axis; null points leave a gap. */
export const knownShareSeries = (
  name: string,
  color: string,
  points: readonly { ms: number; share: number | null }[],
  legendTooltip?: string,
): DateChartSeries => ({
  name,
  type: "spline",
  yAxis: 1,
  dashStyle: "Dash",
  colors: [color],
  data: points.map((p) => ({
    milliseconds: p.ms,
    value: p.share == null ? null : Math.round(p.share * 1000) / 10,
  })),
  custom: {
    ...(legendTooltip != null && { legendTooltip }),
    valueFormatter: (n: number) => formatDecimalToPercent(n / 100, 1),
  },
})
